import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import { Button, Pagination, Select, SelectItem, Tooltip } from '@heroui/react';
import { RiFilterOffLine, RiRefreshLine } from '@remixicon/react';

import { DataTable } from './DataTable';
import { TABLE_CONFIG, createLoadingSkeleton } from './config';

import DefaultLayout from '@/layouts/default';

interface TablePageProps<T> {
  title: React.ReactNode;
  titleActions?: React.ReactNode;
  data: T[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error?: any;
  columns: any[];
  headerColumns: any[];
  sortDescriptor?: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
  renderCell: (item: T, columnKey: React.Key) => React.ReactNode;
  onRefresh?: () => void;
  filtersSection?: React.ReactNode;
  children?: React.ReactNode;
  emptyMessage?: string;
  errorMessage?: string;
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  perPage?: number;
  onPerPageChange?: (perPage: number) => void;
  perPageOptions?: readonly number[];
  // Collapse props
  enableCollapse?: boolean;
  renderCollapseContent?: (item: T) => React.ReactNode;
}

export function TablePage<T>({
  title,
  titleActions,
  data,
  isLoading,
  isError,
  error,
  columns,
  headerColumns,
  sortDescriptor,
  onSortChange,
  renderCell,
  onRefresh,
  filtersSection,
  children,
  emptyMessage = TABLE_CONFIG.ui.emptyStateMessage,
  errorMessage = TABLE_CONFIG.ui.errorStateMessage,
  currentPage,
  totalPages,
  onPageChange,
  perPage,
  onPerPageChange,
  perPageOptions = TABLE_CONFIG.pagination.perPageOptions,
  enableCollapse = false,
  renderCollapseContent
}: TablePageProps<T>) {
  const showPagination = totalPages && totalPages > 1 && onPageChange;
  const showPerPageSelect = Boolean(onPerPageChange);
  const showBottomBar = showPagination || showPerPageSelect;

  return (
    <DefaultLayout>
      <div className="flex h-full flex-col space-y-6 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">{title}</h1>
          {titleActions}
        </div>

        {filtersSection}

        <div className="center-header relative flex h-full flex-col">
          <style>{`
            .center-header th:nth-child(5),
            .center-header th:nth-child(6),
            .center-header th:nth-child(7) {
              text-align: center !important;
            }
          `}</style>
          <div className="flex-1">
            <DataTable
              bottomContent={
                showBottomBar ? (
                  <div className="flex w-full items-center justify-between gap-4 px-2 py-2">
                    {showPerPageSelect ? (
                      <div className="flex items-center gap-2">
                        <span className="text-default-500 text-sm">Rows per page</span>
                        <Select
                          aria-label="Rows per page"
                          className="w-20"
                          selectedKeys={new Set([String(perPage ?? TABLE_CONFIG.pagination.defaultPerPage)])}
                          size="sm"
                          onSelectionChange={(keys) => {
                            const value = Number(Array.from(keys)[0]);
                            if (Number.isFinite(value)) onPerPageChange?.(value);
                          }}
                        >
                          {perPageOptions.map((option) => (
                            <SelectItem key={String(option)} textValue={String(option)}>
                              {option}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                    ) : (
                      <span />
                    )}
                    {showPagination ? (
                      <Pagination
                        isCompact
                        showControls
                        color="primary"
                        page={currentPage || 1}
                        total={totalPages}
                        onChange={onPageChange}
                      />
                    ) : null}
                  </div>
                ) : null
              }
              columns={headerColumns as any}
              emptyContent={isError ? (error as any)?.message || errorMessage : emptyMessage}
              isLoading={isLoading}
              items={data ?? []}
              loadingContent={
                <div className="absolute top-8 flex w-full flex-col justify-start space-y-3 py-4">
                  {createLoadingSkeleton()}
                </div>
              }
              renderCell={renderCell}
              sortDescriptor={sortDescriptor}
              onSortChange={onSortChange}
              enableCollapse={enableCollapse}
              renderCollapseContent={renderCollapseContent}
            />
          </div>
        </div>

        {children}
      </div>
    </DefaultLayout>
  );
}

interface TableFiltersProps {
  children: React.ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  rightSection?: React.ReactNode;
}

export function TableFilters({
  children,
  onRefresh,
  isRefreshing,
  hasActiveFilters,
  onClearFilters,
  rightSection
}: TableFiltersProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex flex-wrap items-end gap-3">{children}</div>
      <div className="flex gap-2">
        {rightSection}
        {onClearFilters && (
          <Tooltip content="Remove all filters">
            <Button color="danger" isDisabled={!hasActiveFilters} size="sm" variant="flat" onPress={onClearFilters}>
              <RiFilterOffLine className="size-5" />
              <span>Remove filters</span>
            </Button>
          </Tooltip>
        )}
        {onRefresh && (
          <Tooltip content="Refresh data">
            <Button isIconOnly color="primary" isLoading={isRefreshing} size="sm" onPress={onRefresh}>
              <RiRefreshLine className="size-5" />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
